/**
 * Cliente das rotas de curadoria do catálogo.
 *
 * A diferença para `features/catalog` — que chega no PR 2b — é o formato: aqui
 * o exercício vem com **todos** os idiomas lado a lado, e não com o texto já
 * resolvido para um. Curar é comparar, e uma vista resolvida esconderia
 * justamente o que precisa ser visto.
 */

import { api } from '@/lib/apiClient';

export const LOCALES = ['pt-BR', 'en-US', 'es-419'] as const;
export type Locale = (typeof LOCALES)[number];

export type PublicationStatus = 'draft' | 'published' | 'archived';
export type TranslationOrigin = 'source' | 'machine' | 'human';

export interface CurationTranslation {
  locale: Locale;
  title: string;
  instructions: string | null;
  origin: TranslationOrigin;
  reviewed: boolean;
}

export interface CurationItem {
  exercise_id: string;
  source: string;
  source_locale: Locale;
  modality: string;
  category: string;
  level: string;
  equipment: string[];
  media_url: string | null;
  media_kind: 'video' | 'image' | null;
  /** Registrado e **não mostrado ao aluno** — só a curadoria vê. */
  media_is_generated: boolean;
  /** De onde a linha veio antes de ser assumida. Não credita ninguém. */
  origin_trace: string | null;
  publication_status: PublicationStatus;
  license_name: string | null;
  license_author: string | null;
  license_url: string | null;
  translations: CurationTranslation[];
  /**
   * O que falta para publicar, calculado pelo servidor.
   *
   * Vem pronto de propósito: reimplementar a regra em TypeScript daria duas
   * cópias que divergiriam na primeira mudança — e a cópia da tela seria a que
   * mentiria, porque a que decide é a do servidor.
   */
  blockers: string[];
}

export interface CurationPage {
  items: CurationItem[];
  total: number;
}

export interface CurationFilters {
  publication_status?: PublicationStatus;
  modality?: string;
  missing_locale?: Locale;
  limit?: number;
  offset?: number;
}

function query(filters: CurationFilters): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  const texto = params.toString();
  return texto ? `?${texto}` : '';
}

export const listForCuration = (filters: CurationFilters = {}) =>
  api.get<CurationPage>(`/admin/exercises${query(filters)}`);

export const getForCuration = (exerciseId: string) =>
  api.get<CurationItem>(`/admin/exercises/${exerciseId}`);

export interface TranslationDraft {
  title: string;
  instructions: string | null;
  reviewed: boolean;
}

/**
 * `origin` não é enviado: o servidor marca `human` por definição.
 *
 * Se a tela pudesse escolher a origem, daria para marcar como humano um texto
 * colado de um tradutor automático — e a regra que segura instrução não
 * revisada perderia o sentido.
 */
export const saveTranslation = (exerciseId: string, locale: Locale, draft: TranslationDraft) =>
  api.put<CurationItem>(`/admin/exercises/${exerciseId}/translations/${locale}`, draft);

export interface ClassificationDraft {
  modality: string;
  category: string;
  level: string;
  equipment: string[];
}

export const saveClassification = (exerciseId: string, draft: ClassificationDraft) =>
  api.put<CurationItem>(`/admin/exercises/${exerciseId}/classification`, draft);

export const setPublication = (exerciseId: string, status: PublicationStatus) =>
  api.put<CurationItem>(`/admin/exercises/${exerciseId}/publication`, {
    publication_status: status,
  });

export interface NewExercise {
  title: string;
  modality: string;
  category: string;
  level: string;
  equipment: string[];
}

/**
 * Cria um exercício no catálogo global.
 *
 * `source` e licença **não** vão no corpo: quem cria aqui cria conteúdo nosso,
 * e a policy do banco recusaria qualquer outra procedência. O que a tela não
 * oferece, ninguém tenta.
 */
export const createExercise = (draft: NewExercise) =>
  api.post<CurationItem>('/admin/exercises', draft);

export interface MediaLimits {
  max_bytes: number;
  accepted: string[];
}

/**
 * Os limites vêm do servidor em vez de repetidos aqui.
 *
 * Duas cópias de "o que é aceito" divergem na primeira mudança, e a cópia da
 * tela é a que mente — quem recusa de verdade é o bucket.
 */
export const getMediaLimits = () => api.get<MediaLimits>('/admin/exercises/media/limits');

/**
 * Envia o arquivo pela nossa API, e não direto para o Storage.
 *
 * É a mesma decisão registrada em `lib/supabase.ts`: o app depende só de
 * `@supabase/auth-js`, sem cliente de dados, para que contornar a API não seja
 * uma linha de código.
 */
export function uploadMedia(exerciseId: string, file: File, isGenerated: boolean) {
  const form = new FormData();
  form.append('file', file);
  form.append('is_generated', String(isGenerated));
  return api.post<CurationItem>(`/admin/exercises/${exerciseId}/media`, form);
}

export const clearMedia = (exerciseId: string) =>
  api.delete<CurationItem>(`/admin/exercises/${exerciseId}/media`);
